import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { getLoginUser, MARKET_TOKEN_KEY } from "@/api/auth";
import WeChatModal from "@/components/WeChatModal";

const navLinks = [
  { label: "首页", href: "#home" },
  { label: "核心优势", href: "#features" },
  { label: "适用场景", href: "#plans" },
  { label: "服务与价格", href: "#pricing" },
  { label: "合规与安全", href: "#compliance" },
  { label: "关于我们", href: "#about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [wechatOpen, setWechatOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(localStorage.getItem(MARKET_TOKEN_KEY)));
  const [currentUser, setCurrentUser] = useState(() => ({
    name: localStorage.getItem("market_name") || "",
  }));

  useEffect(() => {
    if (!isLoggedIn) return;
    let mounted = true;
    getLoginUser().then((user) => {
      if (!mounted) return;
      setCurrentUser({ name: user.name || "" });
      if (user.name) localStorage.setItem("market_name", user.name);
    }).catch(() => {
      // 保留本地缓存，避免用户已登录但接口短暂异常时导航信息消失。
    });
    return () => { mounted = false; };
  }, [isLoggedIn]);

  const displayName = currentUser.name || "未设置名称";

  const handleLogout = () => {
    localStorage.removeItem(MARKET_TOKEN_KEY);
    localStorage.removeItem("market_access");
    localStorage.removeItem("market_name");
    localStorage.removeItem("market_nickname");
    localStorage.removeItem("market_account");
    setIsLoggedIn(false);
    setCurrentUser({ name: "" });
    navigate("/", { replace: true });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header className={`fixed left-0 right-0 top-0 z-40 transition-all duration-300 ${scrolled ? "border-b border-background-200 bg-background-50/95 shadow-[0_2px_12px_rgba(0,0,0,0.04)] backdrop-blur" : "border-b border-transparent bg-transparent"}`}>
      <div className="flex h-16 items-center justify-between px-4 md:h-[72px] md:px-6">
        <a href="#home" className="flex items-center gap-2.5"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-600"><i className="ri-store-3-line text-xl text-background-50" /></span><span className="font-heading text-xl font-bold tracking-tight text-foreground-950">全电易</span></a>
        <nav className="hidden items-center gap-8 lg:flex">{navLinks.map((link) => <a key={link.href} href={link.href} className="whitespace-nowrap text-[15px] font-medium text-foreground-700 transition-colors hover:text-primary-600">{link.label}</a>)}</nav>
        <div className="hidden items-center gap-4 lg:flex">
          <button type="button" onClick={() => setWechatOpen(true)} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 transition-all hover:bg-primary-600"><i className="ri-wechat-line flex h-4 w-4 items-center justify-center" />微信咨询</button>
          <div className="flex items-center gap-3 text-sm">{isLoggedIn ? <><span className="max-w-32 truncate font-medium text-foreground-700" title={displayName}>{displayName}</span><button type="button" onClick={() => navigate("/workspace")} className="rounded-full bg-primary-50 px-3 py-1.5 font-semibold text-primary-700 hover:bg-primary-100">工作台</button><button type="button" onClick={handleLogout} className="inline-flex items-center gap-1 text-foreground-500 hover:text-red-600" title="退出登录"><LogOut size={16} />退出</button></> : <><Link to="/login" className="font-medium text-foreground-700 hover:text-primary-600">登录</Link><span className="text-background-300">|</span><Link to="/register" className="font-medium text-foreground-700 hover:text-primary-600">注册</Link></>}</div>
        </div>
        <button type="button" onClick={() => setOpen((value) => !value)} className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 lg:hidden" aria-label="菜单"><i className={`ri-${open ? "close-line" : "menu-line"} text-2xl`} /></button>
      </div>
      {open && <div className="border-t border-background-200 bg-background-50 px-4 pb-4 lg:hidden"><nav className="flex flex-col gap-1 pt-2">{navLinks.map((link) => <a key={link.href} href={link.href} onClick={() => setOpen(false)} className="py-2.5 text-[15px] font-medium text-foreground-700 hover:text-primary-600">{link.label}</a>)}<button type="button" onClick={() => { setOpen(false); setWechatOpen(true); }} className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50">微信咨询</button><div className="mt-2 flex items-center justify-center gap-4 border-t border-background-200 pt-3 text-sm">{isLoggedIn ? <><span className="font-medium text-foreground-700">{displayName}</span><Link to="/workspace" onClick={() => setOpen(false)} className="font-medium text-primary-600">工作台</Link><button type="button" onClick={() => { setOpen(false); handleLogout(); }} className="inline-flex items-center gap-1 font-medium text-foreground-500 hover:text-red-600"><LogOut size={15} />退出</button></> : <><Link to="/login" onClick={() => setOpen(false)} className="font-medium text-foreground-700">登录</Link><Link to="/register" onClick={() => setOpen(false)} className="font-medium text-primary-600">注册</Link></>}</div></nav></div>}
    </header>
      <WeChatModal open={wechatOpen} onClose={() => setWechatOpen(false)} />
    </>
  );
}
